import { useEffect, useMemo, useState } from "react";
import {
    buildCreateAssignmentPayload,
    validateAssignmentDraft,
} from "../utils/assignmentUtils";

const BOOK_PLACEHOLDER_IMAGE = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="220" height="280" viewBox="0 0 220 280">
  <defs>
    <linearGradient id="cover" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#edf4f1" />
      <stop offset="100%" stop-color="#dbe9e4" />
    </linearGradient>
  </defs>
  <rect width="220" height="280" fill="url(#cover)" />
  <rect x="30" y="30" width="160" height="220" rx="12" fill="#ffffff" stroke="#cddbd5" />
  <rect x="44" y="46" width="16" height="188" rx="6" fill="#1d6257" />
  <rect x="70" y="84" width="108" height="12" rx="6" fill="#d7e7e1" />
  <rect x="70" y="106" width="98" height="10" rx="5" fill="#e6f0ec" />
  <rect x="70" y="125" width="84" height="10" rx="5" fill="#e6f0ec" />
  <text x="124" y="182" fill="#2f5850" font-size="28" font-family="Arial, sans-serif" font-weight="700" text-anchor="middle">BOOK</text>
</svg>
`)}`;

const AssignmentBuilder = ({
    contentOptions,
    studentOptions,
    isLoadingContent,
    isLoadingStudents,
    createError,
    isSubmitting,
    onCreate,
    onPreviewContent,
    resetKey,
    isParent = false,
}) => {
    const [selectedContentId, setSelectedContentId] = useState("");
    const [selectedStudentIds, setSelectedStudentIds] = useState([]);
    const [dueDate, setDueDate] = useState("");
    const [repetitionCount, setRepetitionCount] = useState(1);
    const [aiMode, setAiMode] = useState(false);
    const [errors, setErrors] = useState({});
    const [isStudentsSheetOpen, setIsStudentsSheetOpen] = useState(false);

    useEffect(() => {
        setSelectedContentId("");
        setSelectedStudentIds([]);
        setDueDate("");
        setRepetitionCount(1);
        setAiMode(false);
        setErrors({});
    }, [resetKey]);

    const selectedStudentsSet = useMemo(() => new Set(selectedStudentIds), [selectedStudentIds]);
    const hasManyStudents = studentOptions.length > 4;
    const visibleStudentOptions = hasManyStudents ? studentOptions.slice(0, 4) : studentOptions;
    const learnerSingular = isParent ? "kid" : "student";
    const learnerPlural = isParent ? "kids" : "students";
    const learnerPluralTitle = isParent ? "Kids" : "Students";

    const mapValidationErrors = (validationErrors) => {
        if (!isParent) return validationErrors;

        return Object.fromEntries(
            Object.entries(validationErrors).map(([key, value]) => [
                key,
                String(value)
                    .replace(/students/gi, "kids")
                    .replace(/student/gi, "kid"),
            ])
        );
    };

    const toggleStudent = (studentId) => {
        setSelectedStudentIds((current) => {
            if (current.includes(studentId)) {
                return current.filter((id) => id !== studentId);
            }
            return [...current, studentId];
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const validation = validateAssignmentDraft({
            studentIds: selectedStudentIds,
            readingContentId: selectedContentId,
            dueDate,
            repetitionCount,
        });

        if (!validation.isValid) {
            setErrors(mapValidationErrors(validation.errors));
            return;
        }

        setErrors({});

        await onCreate(
            buildCreateAssignmentPayload({
                studentIds: selectedStudentIds,
                readingContentId: selectedContentId,
                dueDate,
                repetitionCount,
                aiMode,
            })
        );
    };

    return (
        <section className="assignment-builder">
            <header>
                <p className="eyebrow">Create Assignment</p>
                <h2>Assign a reading challenge in three steps</h2>
            </header>

            {createError && (
                <p className="assignments-error" role="alert">
                    {createError}
                </p>
            )}

            <form onSubmit={handleSubmit} className="assignment-builder-form">
                <article className="assignment-step">
                    <h3>Step 1: Select reading content</h3>
                    {isLoadingContent && <p className="assignments-loading">Loading reading content...</p>}
                    {!isLoadingContent && contentOptions.length === 0 && (
                        <p className="assignments-hint">No reading content available yet.</p>
                    )}
                    {!isLoadingContent && contentOptions.length > 0 && (
                        <div className="assignment-content-grid" role="list" aria-label="Reading content options">
                            {contentOptions.map((content) => {
                                const isSelected = selectedContentId === content.id;
                                return (
                                    <article
                                        key={content.id}
                                        className={isSelected ? "assignment-content-card selected" : "assignment-content-card"}
                                        role="listitem"
                                    >
                                        <button
                                            type="button"
                                            className="assignment-content-select"
                                            onClick={() => setSelectedContentId(content.id)}
                                            aria-pressed={isSelected}
                                        >
                                            <div className="assignment-content-cover" aria-hidden="true">
                                                <img
                                                    src={content.imageUrl && content.imageUrl !== "no-image.jpg" ? content.imageUrl : BOOK_PLACEHOLDER_IMAGE}
                                                    alt=""
                                                    loading="lazy"
                                                    onError={(event) => {
                                                        if (event.currentTarget.src !== BOOK_PLACEHOLDER_IMAGE) {
                                                            event.currentTarget.src = BOOK_PLACEHOLDER_IMAGE;
                                                        }
                                                    }}
                                                />
                                            </div>
                                            <div className="assignment-content-copy">
                                                <strong>{content.title}</strong>
                                                <span>{content.author}</span>
                                                <span>Level {content.level}</span>
                                                <span>
                                                    {content.categories?.length ? content.categories.join(", ") : "Uncategorized"}
                                                </span>
                                            </div>
                                        </button>
                                        <button
                                            type="button"
                                            className="assignment-preview-link"
                                            onClick={() => onPreviewContent(content.id)}
                                        >
                                            Preview
                                        </button>
                                    </article>
                                );
                            })}
                        </div>
                    )}
                    {errors.readingContentId && <p className="assignments-error">{errors.readingContentId}</p>}
                </article>

                <article className="assignment-step">
                    <h3>{`Step 2: Select ${learnerPlural}`}</h3>
                    {isLoadingStudents && <p className="assignments-loading">{`Loading ${learnerPlural}...`}</p>}
                    {!isLoadingStudents && studentOptions.length === 0 && (
                        <p className="assignments-hint">{`No ${learnerPlural} available yet.`}</p>
                    )}
                    {!isLoadingStudents && studentOptions.length > 0 && (
                        <div className="assignment-student-grid" role="list" aria-label={`${learnerPluralTitle} options`}>
                            {visibleStudentOptions.map((student) => {
                                const isSelected = selectedStudentsSet.has(student.id);
                                return (
                                    <button
                                        key={student.id}
                                        type="button"
                                        className={isSelected ? "assignment-student-card selected" : "assignment-student-card"}
                                        onClick={() => toggleStudent(student.id)}
                                        aria-pressed={isSelected}
                                    >
                                        <strong>{student.name}</strong>
                                        <span>{student.grade ? `Grade ${student.grade}` : "Grade N/A"}</span>
                                        <span>Code: {student.studentCode || "N/A"}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {!isLoadingStudents && hasManyStudents && (
                        <div className="assignment-step-actions">
                            <button
                                type="button"
                                className="assignment-display-all-btn"
                                onClick={() => setIsStudentsSheetOpen(true)}
                            >
                                Display All
                            </button>
                        </div>
                    )}
                    {errors.studentIds && <p className="assignments-error">{errors.studentIds}</p>}
                </article>

                <article className="assignment-step">
                    <h3>Step 3: Configure assignment</h3>
                    <div className="assignment-config-grid">
                        <label className="lms-field">
                            <span>Due Date</span>
                            <input
                                type="datetime-local"
                                value={dueDate}
                                onChange={(event) => setDueDate(event.target.value)}
                            />
                        </label>
                        <label className="lms-field">
                            <span>Repetition Count</span>
                            <input
                                type="number"
                                min="1"
                                value={repetitionCount}
                                onChange={(event) => setRepetitionCount(event.target.value)}
                            />
                        </label>
                        <label className="assignment-checkbox">
                            <input
                                type="checkbox"
                                checked={aiMode}
                                onChange={(event) => setAiMode(event.target.checked)}
                            />
                            <span>AI Mode</span>
                        </label>
                    </div>
                    {errors.dueDate && <p className="assignments-error">{errors.dueDate}</p>}
                    {errors.repetitionCount && <p className="assignments-error">{errors.repetitionCount}</p>}
                </article>

                <div className="assignment-submit-row">
                    <button type="submit" className="lms-primary-action" disabled={isSubmitting}>
                        {isSubmitting ? "Assigning..." : "Assign reading challenge"}
                    </button>
                </div>
            </form>

            {isStudentsSheetOpen && (
                <div className="assignment-sheet-overlay" role="presentation" onClick={() => setIsStudentsSheetOpen(false)}>
                    <section
                        className="assignment-sheet"
                        role="dialog"
                        aria-modal="true"
                        aria-label={`All ${learnerPlural}`}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <header className="assignment-sheet-header">
                            <h3>{`All ${learnerPlural}`}</h3>
                            <button
                                type="button"
                                className="assignment-sheet-close"
                                onClick={() => setIsStudentsSheetOpen(false)}
                            >
                                Close
                            </button>
                        </header>
                        <div className="assignment-sheet-body">
                            <div className="assignment-student-grid" role="list" aria-label={`All ${learnerSingular} options`}>
                                {studentOptions.map((student) => {
                                    const isSelected = selectedStudentsSet.has(student.id);
                                    return (
                                        <button
                                            key={`sheet-${student.id}`}
                                            type="button"
                                            className={
                                                isSelected ? "assignment-student-card selected" : "assignment-student-card"
                                            }
                                            onClick={() => toggleStudent(student.id)}
                                            aria-pressed={isSelected}
                                        >
                                            <strong>{student.name}</strong>
                                            <span>{student.grade ? `Grade ${student.grade}` : "Grade N/A"}</span>
                                            <span>Code: {student.studentCode || "N/A"}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </section>
                </div>
            )}
        </section>
    );
};

export default AssignmentBuilder;
